import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, Book, Gamepad2, User, Home, Trophy, Flame, Star, ChevronRight, 
  Loader2, X, BookOpen, Calculator, Pencil, FlaskConical, Heart, Shield,
  Languages, Award, Zap, Target, Clock, Gift, Music, Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileStudentApp = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [child, setChild] = useState(null);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Homework scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scanImage, setScanImage] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [explanation, setExplanation] = useState(null);

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch children first
      const childrenRes = await api.get('/api/children');
      const childrenData = childrenRes.data || [];
      
      if (childrenData.length > 0) {
        const childData = childrenData[0];
        setChild(childData);
        
        // Fetch daily tasks, streak, and badges
        const [tasksRes, streakRes] = await Promise.all([
          api.get(`/api/children/${childData.id}/daily-tasks`),
          api.get(`/api/children/${childData.id}/streak`)
        ]);
        
        setDailyTasks(tasksRes.data.tasks || []);
        setStreak(streakRes.data);
        setBadges(childData.badges || []);
        
        // Mock recent activity for now
        setRecentActivity([
          { id: 1, type: 'lesson', title: 'Completed Math Lesson', xp: 25, time: '2h ago' },
          { id: 2, type: 'quiz', title: 'Reading Quiz', xp: 15, time: '4h ago' },
          { id: 3, type: 'badge', title: 'Earned "Fast Learner"', xp: 50, time: 'Yesterday' },
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async (taskId) => {
    try {
      await api.post(`/api/daily-tasks/${taskId}/complete`);
      toast.success('Task completed! +XP earned');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setScanImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const scanHomework = async () => {
    if (!scanImage || !child) return;
    
    setScanning(true);
    try {
      const response = await api.post('/api/homework/scan', {
        image: scanImage,
        child_id: child.id
      });
      setExplanation(response.data);
    } catch (error) {
      // If scan fails, try explain with a generic prompt
      try {
        const explainRes = await api.post('/api/homework/explain', {
          problem: 'Please analyze the homework in the image and explain how to solve it.',
          child_id: child.id
        });
        setExplanation({
          problem: 'Homework Question',
          concept: 'Let me help you understand this!',
          steps: [explainRes.data.explanation],
          hint: 'Take your time and think it through!',
          related_topics: []
        });
      } catch (e) {
        toast.error('Could not analyze homework. Try taking a clearer photo.');
      }
    } finally {
      setScanning(false);
    }
  };

  const explainProblem = async (problem) => {
    if (!child) {
      toast.error('Please wait for your profile to load');
      return;
    }
    
    setScanning(true);
    try {
      console.log('Calling homework explain API...', { problem, child_id: child.id });
      const response = await api.post('/api/homework/explain', {
        problem: problem,
        child_id: child.id
      });
      console.log('Got response:', response.data);
      const newExplanation = {
        problem: problem,
        concept: 'Here\'s how to solve it:',
        steps: [response.data.explanation],
        hint: '',
        related_topics: []
      };
      console.log('Setting explanation:', newExplanation);
      setExplanation(newExplanation);
    } catch (error) {
      console.error('Explain error:', error);
      toast.error('Could not get explanation');
    } finally {
      setScanning(false);
    }
  };

  const getSubjectIcon = (subject) => {
    const icons = {
      reading: '📚', math: '🔢', writing: '✏️', science: '🔬', bible: '📖',
      alphabets: '🔤', counting: '🔢', spanish: '🇪🇸', french: '🇫🇷'
    };
    return icons[subject?.toLowerCase()] || '📝';
  };

  const getSubjectColor = (subject) => {
    const colors = {
      reading: 'from-purple-500 to-purple-600',
      math: 'from-blue-500 to-blue-600',
      writing: 'from-amber-500 to-amber-600',
      science: 'from-emerald-500 to-emerald-600',
      bible: 'from-pink-500 to-pink-600',
      alphabets: 'from-teal-500 to-teal-600',
      spanish: 'from-red-500 to-orange-500',
      french: 'from-indigo-500 to-blue-500'
    };
    return colors[subject?.toLowerCase()] || 'from-slate-500 to-slate-600';
  };

  // All available subjects
  const allSubjects = [
    { id: 'reading', name: 'Reading', icon: '📚', color: 'from-purple-500 to-purple-600' },
    { id: 'math', name: 'Math', icon: '🔢', color: 'from-blue-500 to-blue-600' },
    { id: 'writing', name: 'Writing', icon: '✏️', color: 'from-amber-500 to-amber-600' },
    { id: 'science', name: 'Science', icon: '🔬', color: 'from-emerald-500 to-emerald-600' },
    { id: 'bible', name: 'Bible', icon: '📖', color: 'from-pink-500 to-pink-600' },
    { id: 'safety', name: 'Safety', icon: '🛡️', color: 'from-cyan-500 to-cyan-600' },
  ];

  // Quick actions for the student
  const quickActions = [
    { id: 'early', name: 'ABCs & 123s', icon: '🔤', route: `/early-learning/${child?.id}`, color: 'bg-teal-500' },
    { id: 'stories', name: 'Stories', icon: '📖', route: `/stories/${child?.id}`, color: 'bg-orange-500' },
    { id: 'games', name: 'Games', icon: '🎮', route: `/games/${child?.id}`, color: 'bg-green-500' },
    { id: 'badges', name: 'Badges', icon: '🏆', route: `/badges/${child?.id}`, color: 'bg-yellow-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const completedTasks = dailyTasks.filter(t => t.completed).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white p-4 pb-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Hey {child?.name || 'Student'}! 👋</h1>
            <p className="text-emerald-100 text-sm">Ready to learn something new?</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
            {child?.name?.charAt(0) || '?'}
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex justify-around bg-white/10 rounded-2xl p-3">
          <div className="text-center">
            <div className="text-2xl">🏆</div>
            <div className="text-sm font-medium">Level {child?.current_level || 1}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl">⭐</div>
            <div className="text-sm font-medium">{child?.total_xp || 0} XP</div>
          </div>
          <div className="text-center">
            <div className="text-2xl">🔥</div>
            <div className="text-sm font-medium">{streak?.current_streak || 0} Days</div>
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      <div className="p-4">
        {activeTab === 'home' && (
          <>
            {/* XP Progress to Next Level */}
            <Card className="mb-4 overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Level {child?.current_level || 1} Progress</span>
                  <span className="text-xs text-emerald-600 font-medium">{child?.total_xp % 100 || 0}/100 XP to Level {(child?.current_level || 1) + 1}</span>
                </div>
                <Progress value={(child?.total_xp % 100) || 0} className="h-3" />
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="mb-6">
              <h2 className="text-base font-bold text-slate-800 mb-3">⚡ Quick Start</h2>
              <div className="grid grid-cols-4 gap-2">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => child?.id && navigate(action.route)}
                    className={`${action.color} rounded-2xl p-3 text-white text-center flex flex-col items-center gap-1 hover:opacity-90 active:scale-95 transition-all`}
                    data-testid={`quick-action-${action.id}`}
                  >
                    <span className="text-2xl">{action.icon}</span>
                    <span className="text-xs font-medium">{action.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Daily Tasks */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-800">📋 Today's Tasks</h2>
                <span className="text-sm text-emerald-600 font-medium">{completedTasks}/{dailyTasks.length} done</span>
              </div>
              
              {dailyTasks.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-4xl mb-2">🎉</div>
                    <p className="text-slate-500">No tasks yet! Start learning to get daily challenges.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {dailyTasks.slice(0, 5).map((task) => (
                    <Card 
                      key={task.id} 
                      className={`cursor-pointer transition-all ${task.completed ? 'bg-emerald-50 opacity-70' : 'hover:shadow-md active:scale-[0.98]'}`}
                      onClick={() => !task.completed && completeTask(task.id)}
                      data-testid={`task-${task.id}`}
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">
                          {getSubjectIcon(task.subject)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${task.completed ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                            {task.title || `${task.subject} Task`}
                          </p>
                          <p className="text-xs text-amber-500 font-medium">+{task.xp_reward} XP</p>
                        </div>
                        {task.completed ? (
                          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm">✓</span>
                          </div>
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Subjects Grid */}
            <div className="mb-6">
              <h2 className="text-base font-bold text-slate-800 mb-3">📚 Learn</h2>
              <div className="grid grid-cols-3 gap-2">
                {allSubjects.map((subject) => (
                  <div
                    key={subject.id}
                    className={`bg-gradient-to-br ${subject.color} rounded-2xl p-3 text-white cursor-pointer hover:opacity-90 active:scale-95 transition-all`}
                    onClick={() => child?.id && navigate(`/ai-curriculum/${child.id}/${subject.id}`)}
                    data-testid={`subject-${subject.id}`}
                  >
                    <div className="text-2xl mb-1">{subject.icon}</div>
                    <div className="text-xs font-semibold">{subject.name}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div>
                <h2 className="text-base font-bold text-slate-800 mb-3">📊 Recent Activity</h2>
                <Card>
                  <CardContent className="p-0 divide-y divide-slate-100">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="p-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                          {activity.type === 'lesson' ? '📖' : activity.type === 'quiz' ? '✅' : '🏆'}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">{activity.title}</p>
                          <p className="text-xs text-slate-400">{activity.time}</p>
                        </div>
                        <span className="text-xs font-bold text-emerald-500">+{activity.xp} XP</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {activeTab === 'scan' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800">📷 Homework Helper</h2>
            <p className="text-slate-500 text-sm">Take a photo of your homework for help!</p>
            
            {/* Show explanation result if we have one */}
            {explanation ? (
              <div className="space-y-4">
                <Card className="bg-slate-50">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-slate-800 mb-2">📋 The Problem</h3>
                    <p className="text-slate-600">{explanation.problem}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-slate-800 mb-2">💡 How to solve it:</h3>
                    <div className="space-y-3">
                      {explanation.steps?.map((step, i) => (
                        <div key={i} className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                            {i + 1}
                          </div>
                          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{step}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {explanation.hint && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <h3 className="font-bold text-amber-800 mb-1">🔍 Hint</h3>
                      <p className="text-amber-700 text-sm">{explanation.hint}</p>
                    </CardContent>
                  </Card>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setScanImage(null);
                    setExplanation(null);
                  }}
                >
                  Ask Another Question
                </Button>
              </div>
) : scanImage ? (
              /* Image uploaded - show analyze button */
              <div className="space-y-4">
                <div className="relative">
                  <img src={scanImage} alt="Homework" className="w-full rounded-2xl" />
                  <button 
                    onClick={() => setScanImage(null)}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <Button 
                  className="w-full py-6 text-lg"
                  onClick={scanHomework}
                  disabled={scanning}
                  data-testid="analyze-image-btn"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>✨ Explain This!</>
                  )}
                </Button>
              </div>
            ) : (
              /* Default state - no image, no explanation */
              <div className="space-y-3">
                <label className="block">
                  <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl p-8 text-center cursor-pointer hover:opacity-90 transition">
                    <Camera className="w-16 h-16 mx-auto mb-4 text-white" />
                    <p className="text-white font-semibold text-lg">Take Photo</p>
                    <p className="text-emerald-100 text-sm">or tap to upload</p>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    capture="environment"
                    className="hidden" 
                    onChange={handleImageUpload}
                  />
                </label>
                
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <p className="font-medium text-amber-800 mb-2">📝 Tips for best results:</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Make sure the problem is clearly visible</li>
                      <li>• Hold your phone steady</li>
                      <li>• Use good lighting</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Quick Question Input */}
                <Card>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-700 mb-3">Or type your question:</p>
                    {!child && (
                      <p className="text-red-500 text-sm mb-2">Loading your profile...</p>
                    )}
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const q = e.target.question.value.trim();
                      if (q && child) {
                        explainProblem(q);
                      } else if (!child) {
                        toast.error('Please wait for your profile to load');
                      }
                    }}>
                      <input
                        name="question"
                        type="text"
                        placeholder="e.g., What is 12 x 8?"
                        className="w-full p-3 border rounded-xl mb-3"
                        data-testid="homework-question-input"
                      />
                      <Button type="submit" className="w-full" disabled={scanning || !child} data-testid="get-help-btn">
                        {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {scanning ? 'Thinking...' : 'Get Help'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === 'games' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-slate-800">🎮 Learning Games</h2>
            <p className="text-sm text-slate-500 mb-4">Play fun games while you learn!</p>
            
            {/* Game Categories */}
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className="cursor-pointer hover:shadow-lg active:scale-95 transition-all"
                onClick={() => child?.id && navigate(`/digital-safety-games/${child.id}`)}
              >
                <CardContent className="p-4 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800">Safety Games</p>
                  <p className="text-xs text-slate-500">Learn online safety</p>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-lg active:scale-95 transition-all"
                onClick={() => child?.id && navigate(`/games/${child.id}?subject=math`)}
              >
                <CardContent className="p-4 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center">
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800">Math Games</p>
                  <p className="text-xs text-slate-500">Numbers & counting</p>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-lg active:scale-95 transition-all"
                onClick={() => child?.id && navigate(`/games/${child.id}?subject=reading`)}
              >
                <CardContent className="p-4 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center">
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800">Word Games</p>
                  <p className="text-xs text-slate-500">Reading & vocabulary</p>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-lg active:scale-95 transition-all"
                onClick={() => child?.id && navigate(`/early-learning/${child.id}`)}
              >
                <CardContent className="p-4 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center">
                    <Music className="w-7 h-7 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800">ABC Song</p>
                  <p className="text-xs text-slate-500">Sing & learn</p>
                </CardContent>
              </Card>
            </div>

            {/* Challenge Card */}
            <Card className="bg-gradient-to-r from-amber-400 to-orange-500 text-white mt-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold">Daily Challenge</p>
                    <p className="text-sm text-amber-100">Complete 3 games for bonus XP!</p>
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => child?.id && navigate(`/challenges/${child.id}`)}
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                  >
                    Play
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Profile Header */}
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 mx-auto mb-4 flex items-center justify-center text-4xl text-white font-bold shadow-lg">
                {child?.name?.charAt(0) || '?'}
              </div>
              <h2 className="text-2xl font-bold text-slate-800">{child?.name}</h2>
              <p className="text-slate-500 mb-2">Level {child?.current_level || 1} Learner</p>
              <div className="flex items-center justify-center gap-1 text-sm text-amber-500">
                <Flame className="w-4 h-4" />
                <span>{streak?.current_streak || 0} day streak</span>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl mb-1">⭐</div>
                  <div className="text-lg font-bold text-slate-800">{child?.total_xp || 0}</div>
                  <div className="text-xs text-slate-500">Total XP</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl mb-1">🔥</div>
                  <div className="text-lg font-bold text-slate-800">{streak?.current_streak || 0}</div>
                  <div className="text-xs text-slate-500">Day Streak</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl mb-1">🏆</div>
                  <div className="text-lg font-bold text-slate-800">{streak?.longest_streak || 0}</div>
                  <div className="text-xs text-slate-500">Best Streak</div>
                </CardContent>
              </Card>
            </div>

            {/* Badges */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800">🏅 My Badges</h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => child?.id && navigate(`/badges/${child.id}`)}
                    className="text-emerald-600"
                  >
                    View All
                  </Button>
                </div>
                {badges.length > 0 ? (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {badges.slice(0, 5).map((badge, idx) => (
                      <div key={idx} className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xl">
                        🏅
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Complete lessons to earn badges!</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardContent className="p-0 divide-y divide-slate-100">
                <button 
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => child?.id && navigate(`/rewards/${child.id}`)}
                >
                  <span className="flex items-center gap-3">
                    <Gift className="w-5 h-5 text-amber-500" />
                    <span>My Rewards</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
                <button 
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => child?.id && navigate(`/leaderboard/${child.id}`)}
                >
                  <span className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span>Leaderboard</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
                <button 
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 active:bg-slate-100"
                  onClick={() => navigate('/dashboard')}
                >
                  <span className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <span>Parent Dashboard</span>
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-around">
        {[
          { id: 'home', icon: Home, label: 'Home' },
          { id: 'scan', icon: Camera, label: 'Scan' },
          { id: 'games', icon: Gamepad2, label: 'Games' },
          { id: 'profile', icon: User, label: 'Profile' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
              activeTab === tab.id 
                ? 'text-emerald-600 bg-emerald-50' 
                : 'text-slate-400'
            }`}
          >
            <tab.icon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MobileStudentApp;
